/* =========================================================================
   PixelBrew v2 — the whole theme's JavaScript.

   Vanilla, deferred, no dependencies. Everything here is an enhancement:
   with JS disabled the cart button is a link to /cart, product forms post
   normally, quick-add posts normally, the catalog shows every design and
   the drawer / mobile panel / modal are hidden by CSS (.no-js).
   ========================================================================= */
(function () {
  'use strict';

  var doc = document;
  var body = doc.body;
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  var strings = {};
  try {
    var node = doc.querySelector('[data-pb-strings]');
    if (node) strings = JSON.parse(node.textContent);
  } catch (e) { /* strings stay empty; nothing here is load-bearing */ }

  function $(sel, root) { return (root || doc).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); }

  function announce(message) {
    var live = $('[data-pb-live]');
    if (live && message) { live.textContent = ''; live.textContent = message; }
  }

  /* --- Overlay layers: drawer, mobile panel, modal ----------------------- */

  var openLayer = null;
  var lastFocus = null;

  function trap(event) {
    if (!openLayer || event.key !== 'Tab') return;
    var items = $$(FOCUSABLE, openLayer).filter(function (el) {
      return el.offsetWidth || el.offsetHeight || el.getClientRects().length;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && openLayer) { closeLayer(); return; }
    trap(event);
  }

  function openLayerEl(el) {
    if (!el || openLayer === el) return;
    if (openLayer) closeLayer();
    lastFocus = doc.activeElement;
    openLayer = el;
    el.hidden = false;
    // Next frame so the transform transition actually runs.
    requestAnimationFrame(function () { el.classList.add('is-open'); });
    body.classList.add('is-locked');
    var target = $('[data-autofocus]', el) || $(FOCUSABLE, el);
    if (target) target.focus();
    doc.addEventListener('keydown', onKeydown);
  }

  function closeLayer() {
    var el = openLayer;
    if (!el) return;
    openLayer = null;
    el.classList.remove('is-open');
    body.classList.remove('is-locked');
    doc.removeEventListener('keydown', onKeydown);
    var hide = function () { if (!el.classList.contains('is-open')) el.hidden = true; };
    setTimeout(hide, 240);
    if (lastFocus && doc.contains(lastFocus)) lastFocus.focus();
    lastFocus = null;
    $$('[aria-expanded="true"][data-mobile-nav-toggle]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
  }

  /* --- Cart: AJAX add, re-render, quantity, remove ----------------------- */

  /*
   * The cart UI is re-rendered through the Section Rendering API, bundled
   * into the mutation itself: /cart/add.js and /cart/change.js accept a
   * `sections` param and return the freshly rendered HTML in `sections`.
   *
   * Never fetch `<current path>?sections=` with `Accept: application/json`.
   * Shopify answers that header with the page's own JSON representation
   * BEFORE it looks at ?sections=: /products/<h> returns {product}, /cart
   * returns the cart, and /collections/all has no JSON representation at
   * all, so it returns 404. That made every collection-page quick-add
   * report a failure after the item had already been added.
   *
   * An add or change is only reported as failed when the mutation itself
   * fails (network error, non-2xx, Shopify 422 body). If the add succeeded
   * but the cart UI can't be re-rendered, we degrade to a second section
   * fetch, then to a count-only update from /cart.js. Never an error.
   */
  var CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /** Parse a cart AJAX response. Resolves with the JSON body (or {}) on 2xx;
   *  rejects otherwise. Only a 422 carries a customer-facing description
   *  ("… is sold out", "You can't add more …"); anything else gets the
   *  generic theme string. */
  function readCartResponse(response) {
    return response.text().then(function (text) {
      var data = null;
      try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
      if (!response.ok) {
        var described = response.status === 422 && data && typeof data.description === 'string' && data.description;
        throw new Error(described || '');
      }
      return data || {};
    });
  }

  /** Swap rendered section HTML into the page. True only when both the cart
   *  button and the drawer were present in the payload and swapped. */
  function applyCartSections(sections) {
    if (!sections || typeof sections !== 'object') return false;
    var parser = new DOMParser();
    var swapped = 0;

    if (typeof sections['cart-icon-bubble'] === 'string') {
      var newBtn = $('#pb-cart-button', parser.parseFromString(sections['cart-icon-bubble'], 'text/html'));
      var oldBtn = $('#pb-cart-button');
      if (newBtn && oldBtn) { oldBtn.replaceWith(newBtn); swapped++; }
    }

    if (typeof sections['cart-drawer'] === 'string') {
      var newDrawer = $('#PBCartDrawer', parser.parseFromString(sections['cart-drawer'], 'text/html'));
      var oldDrawer = $('#PBCartDrawer');
      if (newDrawer && oldDrawer) {
        var wasOpen = oldDrawer.classList.contains('is-open');
        if (openLayer === oldDrawer) openLayer = null;
        oldDrawer.replaceWith(newDrawer);
        newDrawer.hidden = true;
        newDrawer.classList.remove('is-open');
        if (wasOpen) openLayerEl(newDrawer);
        swapped++;
      }
    }

    return swapped === 2;
  }

  /** Last resort: the cart changed but no fresh HTML could be rendered. Keep
   *  the count honest from /cart.js; the drawer catches up on next load. */
  function updateCountFromCart() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || typeof cart.item_count !== 'number') return;
        $$('#pb-cart-button .pb-cart-btn__count').forEach(function (el) {
          el.textContent = String(cart.item_count);
          el.classList.toggle('is-filled', cart.item_count > 0);
        });
      })
      .catch(function () { /* nothing left to try; the next page load is correct */ });
  }

  /** Re-render the cart UI after a successful mutation. Always resolves (with
   *  true when the drawer HTML is fresh), so a render problem can never be
   *  mistaken for a failed add. */
  function renderCart(sections, openDrawer) {
    return Promise.resolve()
      .then(function () {
        if (applyCartSections(sections)) return true;
        // No Accept override: with the default header every storefront path
        // answers ?sections= with the sections map. The root is used because
        // it has no JSON representation to compete with it.
        return fetch(ROOT + '?sections=' + CART_SECTIONS.join(','))
          .then(function (r) { if (!r.ok) throw new Error('sections ' + r.status); return r.json(); })
          .then(applyCartSections);
      })
      .then(function (fresh) {
        if (!fresh) throw new Error('cart sections unavailable');
        if (openDrawer) {
          var drawer = $('[data-cart-drawer]');
          if (drawer && openLayer !== drawer) openLayerEl(drawer);
        }
        return true;
      })
      .catch(function () {
        return updateCountFromCart().then(function () { return false; });
      });
  }

  function addToCart(formData, button) {
    var label = button ? button.querySelector('[data-add-label],[data-quick-label]') : null;
    var previous = label ? label.textContent : '';
    if (button) button.setAttribute('disabled', 'disabled');
    if (label && strings.adding) label.textContent = strings.adding;

    formData.append('sections', CART_SECTIONS.join(','));

    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript' },
      body: formData
    })
      .then(readCartResponse)
      .then(
        function (item) {
          announce(strings.added || '');
          return renderCart(item.sections, true).then(function (drawerOpened) {
            // Round 2: the toast reads the drawer that was JUST rendered, so
            // its numbers are the server's, never a client-side guess. It is
            // strictly after the success path; the error branch never gets here.
            showToast(toastText(readShipBar($('[data-cart-drawer]')), strings));
            // Drawer couldn't be shown: confirm on the button instead.
            if (!drawerOpened && label && strings.added) {
              label.textContent = strings.added;
              return new Promise(function (resolve) { setTimeout(resolve, 1500); });
            }
          });
        },
        function (error) {
          // Only the add request itself lands here (network, non-2xx, 422).
          var message = error.message || strings.cartError || 'Sorry, that could not be added to your cart.';
          announce(message);
          window.alert(message);
        }
      )
      .then(function () {
        if (button) button.removeAttribute('disabled');
        if (label) label.textContent = previous;
      });
  }

  /* --- Round 2: add-to-cart toast ---------------------------------------- */

  /*
   * The toast is the visible twin of the live-region announcement. Its
   * number is never computed here: the freshly rendered cart drawer carries
   * the server's free-shipping arithmetic on [data-pb-ship-bar] (remaining
   * cents and the money-formatted remaining), and the toast only reads it.
   * No threshold configured = no ship-bar = toast says "Item acquired" alone.
   */

  /** Pull the ship-bar numbers out of a rendered drawer (or null). */
  function readShipBar(root) {
    var bar = root ? $('[data-pb-ship-bar]', root) : null;
    if (!bar) return null;
    var remaining = parseInt(bar.getAttribute('data-pb-remaining'), 10);
    if (isNaN(remaining)) return null;
    return { remaining: remaining, remainingMoney: bar.getAttribute('data-pb-remaining-money') || '' };
  }

  /** Pure: toast text from the ship-bar reading and the published strings.
   *  Exposed on window.PB for the spec file; nothing else depends on it. */
  function toastText(ship, s) {
    var head = (s && s.toast_acquired) || '';
    if (!ship) return head;
    if (ship.remaining <= 0) return join(head, (s && s.toast_unlocked) || '');
    if (!ship.remainingMoney) return head;
    var tail = ((s && s.toast_to_free_shipping) || '%AMOUNT%').replace('%AMOUNT%', ship.remainingMoney);
    return join(head, tail);
  }

  function join(a, b) { return a && b ? a + ' \u00b7 ' + b : (a || b); }

  var toastTimer = null;

  /** Show the single toast (replacing whatever is up: no stacking), announce
   *  it politely, auto-dismiss after 4 s, or sooner from the close button. */
  function showToast(text) {
    var toast = $('[data-pb-toast]');
    var textEl = toast && $('[data-pb-toast-text]', toast);
    if (!toast || !textEl || !text) return;
    if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
    textEl.textContent = text;
    toast.hidden = false;
    // Restart the enter animation even when a toast was already up.
    toast.classList.remove('is-open');
    void toast.offsetWidth;
    toast.classList.add('is-open');
    announce(text);
    toastTimer = setTimeout(hideToast, 4000);
  }

  function hideToast() {
    var toast = $('[data-pb-toast]');
    if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
    if (!toast) return;
    toast.classList.remove('is-open');
    toast.hidden = true;
  }

  doc.addEventListener('click', function (event) {
    if (event.target.closest('[data-pb-toast-close]')) { event.preventDefault(); hideToast(); }
  });

  window.PB = window.PB || {};
  window.PB.toastText = toastText;
  window.PB.readShipBar = readShipBar;

  function changeLine(line, quantity) {
    return fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity, sections: CART_SECTIONS })
    })
      .then(readCartResponse)
      .then(
        function (cart) { return renderCart(cart.sections, false); },
        function (error) {
          // e.g. 422 over the inventory limit: say why, then re-render so the
          // stepper shows the quantity the cart really holds.
          announce(error.message || strings.cartError || '');
          return renderCart(null, false);
        }
      );
  }

  /* --- Delegated click + submit handlers -------------------------------- */

  doc.addEventListener('click', function (event) {
    var el;

    el = event.target.closest('[data-cart-drawer-open]');
    if (el) {
      var drawer = $('[data-cart-drawer]');
      if (drawer) { event.preventDefault(); openLayerEl(drawer); }
      return;
    }

    if (event.target.closest('[data-cart-drawer-close], [data-mobile-nav-close], [data-exit-intent-close]')) {
      event.preventDefault();
      closeLayer();
      return;
    }

    el = event.target.closest('[data-mobile-nav-toggle]');
    if (el) {
      var nav = $('[data-mobile-nav]');
      if (nav) { el.setAttribute('aria-expanded', 'true'); openLayerEl(nav); }
      return;
    }

    el = event.target.closest('[data-qty-step]');
    if (el) {
      var stepper = el.closest('[data-qty-stepper]');
      var input = stepper && stepper.querySelector('input[type="number"]');
      if (!input) return;
      var min = parseInt(input.min || '1', 10);
      var next = (parseInt(input.value, 10) || min) + parseInt(el.getAttribute('data-qty-step'), 10);
      if (next < min) next = min;
      input.value = next;
      var line = input.getAttribute('data-line');
      if (line) changeLine(parseInt(line, 10), next);
      return;
    }

    el = event.target.closest('[data-line-remove]');
    if (el) {
      event.preventDefault();
      changeLine(parseInt(el.getAttribute('data-line'), 10), 0);
      return;
    }

    el = event.target.closest('[data-gallery-thumb]');
    if (el) { selectSlide(el.closest('[data-pb-gallery]'), el.getAttribute('data-index')); return; }

    el = event.target.closest('[data-sticky-add]');
    if (el) {
      var form = $('#PBProductForm');
      if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
      return;
    }
  });

  doc.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches('form[action*="/cart/add"]')) return;
    event.preventDefault();
    addToCart(new FormData(form), form.querySelector('button[type="submit"]'));
  });

  doc.addEventListener('change', function (event) {
    var input = event.target;
    if (input.matches('[data-qty-input]')) {
      var line = parseInt(input.getAttribute('data-line'), 10);
      var qty = parseInt(input.value, 10);
      if (!isNaN(line) && !isNaN(qty)) changeLine(line, qty);
    }
  });

  /* --- Product: variant picker + gallery -------------------------------- */

  function setGroupVisibility(root, group) {
    $$('[data-group]', root).forEach(function (el) {
      if (el.matches('[data-variant-radio]')) return;
      var g = el.getAttribute('data-group');
      el.hidden = !(g === 'shared' || g === group);
    });
  }

  function selectSlide(gallery, index) {
    if (!gallery) return;
    var slides = $$('[data-gallery-slide]', gallery);
    var match = slides.filter(function (s) { return s.getAttribute('data-index') === String(index); })[0];
    if (!match || match.hidden) return;
    slides.forEach(function (s) { s.classList.toggle('is-active', s === match); });
    $$('[data-gallery-thumb]', gallery).forEach(function (t) {
      t.classList.toggle('is-active', t.getAttribute('data-index') === String(index));
    });
    $$('[data-gallery-dot]', gallery).forEach(function (d) {
      d.classList.toggle('is-active', d.getAttribute('data-index') === String(index));
    });
    if (window.matchMedia('(max-width: 899px)').matches) {
      match.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
    }
  }

  function firstVisibleIndex(gallery) {
    var visible = $$('[data-gallery-slide]', gallery).filter(function (s) { return !s.hidden; });
    return visible.length ? visible[0].getAttribute('data-index') : null;
  }

  function initProduct() {
    var pdp = $('[data-pb-product]');
    if (!pdp) return;

    var gallery = $('[data-pb-gallery]', pdp);
    var priceEl = $('[data-pb-price]', pdp);
    var stickyPrice = $('[data-sticky-price]', pdp);
    var addBtn = $('[data-add-to-cart]', pdp);
    var addLabel = addBtn ? $('[data-add-label]', addBtn) : null;
    var stickyAdd = $('[data-sticky-add]', pdp);

    // The Add button follows the selected Mug Variant. Without this, a page
    // opened on a sold-out finish stayed "Sold out" after picking one that
    // can be bought (and vice versa).
    function syncBuyState(radio) {
      var available = radio.getAttribute('data-available') === 'true';
      var text = available ? strings.addToCart : strings.soldOut;
      if (addBtn) addBtn.disabled = !available;
      if (addLabel && text) addLabel.textContent = text;
      if (stickyAdd) {
        stickyAdd.disabled = !available;
        if (text) stickyAdd.textContent = text;
      }
    }

    $$('[data-variant-radio]', pdp).forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        var group = radio.getAttribute('data-group');
        pdp.setAttribute('data-current-group', group);
        setGroupVisibility(pdp, group);
        syncBuyState(radio);

        var price = radio.getAttribute('data-price');
        if (price) {
          if (priceEl) priceEl.textContent = price;
          if (stickyPrice) stickyPrice.textContent = price;
        }

        if (gallery) {
          var active = $$('[data-gallery-slide]', gallery).filter(function (s) {
            return s.classList.contains('is-active') && !s.hidden;
          })[0];
          if (!active) selectSlide(gallery, firstVisibleIndex(gallery));
        }

        if (window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', radio.value);
          window.history.replaceState({}, '', url);
        }
      });
    });

    if (gallery) {
      // Make sure something is shown even if no variant image matched.
      if (!$('[data-gallery-slide].is-active:not([hidden])', gallery)) {
        selectSlide(gallery, firstVisibleIndex(gallery));
      }

      var thumbs = $('[data-gallery-thumbs]', gallery);
      if (thumbs) {
        thumbs.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          var list = $$('[data-gallery-thumb]', thumbs).filter(function (t) { return !t.hidden; });
          var at = list.indexOf(doc.activeElement);
          if (at === -1) return;
          event.preventDefault();
          var next = event.key === 'ArrowRight' ? at + 1 : at - 1;
          if (next < 0) next = list.length - 1;
          if (next >= list.length) next = 0;
          list[next].focus();
          selectSlide(gallery, list[next].getAttribute('data-index'));
        });
      }

      var stage = $('[data-gallery-stage]', gallery);
      if (stage) {
        var raf = null;
        stage.addEventListener('scroll', function () {
          if (raf) cancelAnimationFrame(raf);
          raf = requestAnimationFrame(function () {
            var mid = stage.scrollLeft + stage.clientWidth / 2;
            $$('[data-gallery-slide]', stage).forEach(function (s) {
              if (s.hidden) return;
              var inside = s.offsetLeft <= mid && s.offsetLeft + s.offsetWidth > mid;
              $$('[data-gallery-dot]', gallery).forEach(function (d) {
                if (d.getAttribute('data-index') === s.getAttribute('data-index')) {
                  d.classList.toggle('is-active', inside);
                }
              });
            });
          });
        }, { passive: true });
      }
    }

    var sticky = $('[data-sticky-buy]', pdp);
    var mainBtn = $('[data-add-to-cart]', pdp);
    if (sticky && mainBtn && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        sticky.hidden = entries[0].isIntersecting || !!openLayer;
      }, { rootMargin: '0px 0px -40px 0px' }).observe(mainBtn);
    }
  }

  /* --- Collection: client-side chips + sort ------------------------------ */

  function initCollection() {
    var root = $('[data-pb-collection]');
    if (!root) return;

    var grid = $('[data-pb-grid]', root);
    var showing = $('[data-pb-showing]', root);
    var noResults = $('[data-pb-no-results]', root);
    var sort = $('[data-pb-sort]', root);
    if (!grid) return;

    var cards = $$('[data-pb-card]', grid);
    var order = cards.slice();

    function applyFromUrl(push) {
      var params = new URLSearchParams(window.location.search);
      var quest = params.get('quest') || '';
      var preset = params.get('preset') || '';
      var loot = params.get('loot') === '1';
      var saved = loot ? (lootRead() || []) : [];
      var shown = 0;

      cards.forEach(function (card) {
        var ok = (!quest || card.getAttribute('data-quest') === quest) &&
                 (!preset || card.getAttribute('data-preset') === preset) &&
                 (!loot || saved.indexOf(card.getAttribute('data-num')) > -1);
        card.hidden = !ok;
        if (ok) shown++;
      });

      $$('[data-chip]', root).forEach(function (chip) {
        var type = chip.getAttribute('data-filter-type');
        var value = chip.getAttribute('data-filter-value');
        var active = (type === 'all' && !quest && !preset && !loot) ||
                     (type === 'quest' && quest === value) ||
                     (type === 'preset' && preset === value) ||
                     (type === 'loot' && loot);
        chip.classList.toggle('is-active', active);
        if (active) chip.setAttribute('aria-current', 'true');
        else chip.removeAttribute('aria-current');
      });

      var lootEmpty = $('[data-pb-loot-empty]', root);
      if (lootEmpty) lootEmpty.hidden = !(loot && shown === 0);
      if (noResults) noResults.hidden = shown !== 0 || (loot && shown === 0);
      if (showing) {
        var one = showing.getAttribute('data-one') || '';
        var other = showing.getAttribute('data-other') || '';
        showing.textContent = shown === 1 ? one : other.replace(/\d+/, String(shown));
      }
      if (push) announce(showing ? showing.textContent : '');
    }

    $$('[data-chip]', root).forEach(function (chip) {
      chip.addEventListener('click', function (event) {
        event.preventDefault();
        window.history.pushState({}, '', chip.getAttribute('href'));
        applyFromUrl(true);
      });
    });
    doc.addEventListener('pb:loot-changed', function () { applyFromUrl(false); });

    if (sort) {
      sort.addEventListener('change', function () {
        var mode = sort.value;
        var sorted = order.slice();
        if (mode === 'in-stock') {
          // Stable: in-stock cards first, original order inside each group.
          sorted.sort(function (a, b) {
            var av = a.getAttribute('data-available') === 'true' ? 0 : 1;
            var bv = b.getAttribute('data-available') === 'true' ? 0 : 1;
            return av - bv || order.indexOf(a) - order.indexOf(b);
          });
        }
        else if (mode === 'number') sorted.sort(byNumber('data-num', 1));
        else if (mode === 'newest') sorted.sort(byNumber('data-created', -1));
        else if (mode === 'title-asc') {
          sorted.sort(function (a, b) {
            return a.getAttribute('data-title').localeCompare(b.getAttribute('data-title'));
          });
        }
        sorted.forEach(function (card) { grid.appendChild(card); });
      });
    }

    function byNumber(attr, dir) {
      return function (a, b) {
        return (parseInt(a.getAttribute(attr), 10) - parseInt(b.getAttribute(attr), 10)) * dir;
      };
    }

    /* Finish view: show every card in White or Black. The card's photo
       and its WHOLE quick row (label, swatch, variant id, add / sold-out
       state) swap together from the server-rendered <template>, so they
       always name the same mug. Cards with no photo for that finish keep
       their own. The card's sold-out stamp follows the row shown. */
    var finishView = $('[data-pb-finishview]', root);
    if (finishView) {
      finishView.hidden = false;
      finishView.addEventListener('change', function (event) {
        if (!event.target || event.target.name !== 'finishview') return;
        var want = event.target.value;
        cards.forEach(function (card) {
          var img = $('.pb-card__media .pb-plate__img', card);
          var target = want || card.getAttribute('data-finish');
          var src = card.getAttribute('data-' + target + '-src');
          var alt = card.getAttribute('data-' + target + '-alt');
          if (!src) return; // no photo for that finish: leave the card alone

          var live = $('.pb-card__quick', card);
          if (live && live.getAttribute('data-finish') !== target) {
            var tpl = $$('template[data-quick-alt]', card).filter(function (t) {
              return t.getAttribute('data-finish') === target;
            })[0];
            if (!tpl) return;
            // Park the live row in a template of its own, then mount the wanted one.
            var park = doc.createElement('template');
            park.setAttribute('data-quick-alt', '');
            park.setAttribute('data-finish', live.getAttribute('data-finish'));
            park.content.appendChild(live);
            var next = tpl.content.firstElementChild;
            tpl.parentNode.replaceChild(park, tpl);
            card.appendChild(next);
            var available = next.getAttribute('data-available') === 'true';
            card.classList.toggle('is-soldout', !available);
            card.setAttribute('data-available', available ? 'true' : 'false');
            var stamp = $('.pb-card__stamp', card);
            if (available && stamp) stamp.remove();
            if (!available && !stamp) {
              var s = doc.createElement('span');
              s.className = 'pb-card__stamp';
              s.setAttribute('aria-hidden', 'true');
              s.textContent = strings.soldOut || 'Sold out';
              $('.pb-card__media', card).appendChild(s);
            }
          }
          if (img && img.tagName === 'IMG' && img.getAttribute('src') !== src) {
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            img.src = src;
            img.alt = alt || img.alt;
          }
        });
        var checked = finishView.querySelector('input:checked');
        var label = checked && checked.parentNode ? checked.parentNode.textContent.trim() : '';
        announce(label);
      });
    }

    window.addEventListener('popstate', function () { applyFromUrl(false); });
    applyFromUrl(false);
  }

  /* --- FAQ accordion: one panel open at a time --------------------------- */

  function initAccordion() {
    var group = $('[data-pb-accordion]');
    if (!group) return;
    $$('details', group).forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        $$('details', group).forEach(function (other) {
          if (other !== item) other.open = false;
        });
      });
    });
  }

  /* --- Exit-intent guild modal ------------------------------------------ */

  function initExitIntent() {
    var modal = $('[data-exit-intent]');
    if (!modal) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    var path = window.location.pathname;
    if (path.indexOf('/cart') === 0 || path.indexOf('/checkout') === 0 || path.indexOf('/challenge') === 0) return;

    var KEY = 'pb-exit-intent-seen';
    try {
      var seen = parseInt(window.localStorage.getItem(KEY) || '0', 10);
      if (seen && Date.now() - seen < 30 * 24 * 60 * 60 * 1000) return;
    } catch (e) { return; }

    var armed = false;
    setTimeout(function () { armed = true; }, 20000);

    doc.addEventListener('mouseout', function (event) {
      if (!armed || openLayer || event.relatedTarget || event.clientY > 12) return;
      armed = false;
      try { window.localStorage.setItem(KEY, String(Date.now())); } catch (e) { /* private mode */ }
      openLayerEl(modal);
    });
  }

  /* --- V4 loot: saved designs, kept only in this browser ------------------ */

  /* A visitor's saved designs live in localStorage under one key, as a
     list of design numbers. Nothing leaves the device, nothing is sent to
     Shopify, no account is involved. With storage unavailable (private
     mode, blocked) the hearts simply stay hidden: the markup ships them
     `hidden` and only this function reveals them. */
  var LOOT_KEY = 'pb-loot-v1';

  function lootRead() {
    try {
      var raw = window.localStorage.getItem(LOOT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (n) { return /^\d{2}$/.test(String(n)); }) : [];
    } catch (e) { return null; }
  }

  function lootWrite(list) {
    try { window.localStorage.setItem(LOOT_KEY, JSON.stringify(list)); return true; }
    catch (e) { return false; }
  }

  function initLoot() {
    var saved = lootRead();
    if (saved === null) return; // storage unavailable: hearts stay hidden
    try { window.localStorage.setItem(LOOT_KEY + '-probe', '1'); window.localStorage.removeItem(LOOT_KEY + '-probe'); }
    catch (e) { return; }

    var toggles = $$('[data-loot-toggle]');
    var counts = $$('[data-loot-count]');
    var lootChip = $('[data-chip][data-filter-type="loot"]');
    var onLabel = strings.loot_saved || 'Saved';
    var offLabel = strings.loot_save || 'Save';

    function paint() {
      toggles.forEach(function (btn) {
        var on = saved.indexOf(btn.getAttribute('data-loot-num')) > -1;
        btn.classList.toggle('is-on', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var label = $('[data-loot-label]', btn);
        if (label) label.textContent = on ? onLabel : offLabel;
      });
      counts.forEach(function (el) {
        el.textContent = String(saved.length);
        el.hidden = saved.length === 0;
      });
      // The "My loot" chip only appears once there is loot to show.
      if (lootChip) lootChip.hidden = saved.length === 0 && !lootChip.classList.contains('is-active');
    }

    toggles.forEach(function (btn) {
      btn.hidden = false;
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        var num = btn.getAttribute('data-loot-num');
        var at = saved.indexOf(num);
        if (at > -1) saved.splice(at, 1); else saved.push(num);
        if (!lootWrite(saved)) return;
        paint();
        announce((at > -1 ? (strings.loot_removed || 'Removed from your loot') : (strings.loot_added || 'Saved to your loot')));
        // In the catalog's loot view, un-saving a card removes it from view.
        doc.dispatchEvent(new CustomEvent('pb:loot-changed'));
      });
    });

    paint();
  }

  /* --- V4 hero: strip tiles swap into the featured slot ------------------ */

  function initHero() {
    var hero = $('[data-pb-hero]');
    if (!hero) return;
    var featured = $('[data-hero-featured]', hero);
    var pick = $('[data-hero-pick]', featured || hero);
    var tiles = $$('[data-hero-tile]', hero);
    if (!featured || !pick || tiles.length < 2) return;

    var img = $('.pb-plate__img', pick);
    var num = $('[data-hero-num]', pick);
    var name = $('[data-hero-name]', pick);
    var price = $('[data-hero-price]', pick);
    var current = null;
    var timer = null;

    function show(tile) {
      if (!tile || tile === current) return;
      current = tile;
      tiles.forEach(function (t) { t.classList.toggle('is-current', t === tile); });
      hero.style.setProperty('--pb-hero-accent', tile.getAttribute('data-accent'));
      pick.style.setProperty('--pb-accent', tile.getAttribute('data-accent'));
      pick.setAttribute('href', tile.getAttribute('href'));
      if (num) num.textContent = '#' + tile.getAttribute('data-num');
      if (name) name.textContent = tile.getAttribute('data-name');
      if (price) price.textContent = tile.getAttribute('data-price');
      if (img && img.tagName === 'IMG') {
        var src = tile.getAttribute('data-src');
        if (src && img.getAttribute('src') !== src) {
          featured.classList.add('is-swapping');
          var next = new Image();
          next.onload = next.onerror = function () {
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            img.src = src;
            img.alt = tile.getAttribute('data-alt') || '';
            featured.classList.remove('is-swapping');
          };
          next.src = src;
        }
      }
    }

    tiles.forEach(function (tile) {
      // Hover and focus preview; a click on a tile goes to the product, as
      // the markup says, unless it is a tap on a coarse pointer, where the
      // first tap previews and the second (now current) tap follows the link.
      tile.addEventListener('mouseenter', function () {
        clearTimeout(timer);
        timer = setTimeout(function () { show(tile); }, 80);
      });
      tile.addEventListener('focus', function () { show(tile); });
      tile.addEventListener('click', function (event) {
        if (window.matchMedia('(pointer: coarse)').matches && tile !== current) {
          event.preventDefault();
          show(tile);
        }
      });
    });

    current = tiles.filter(function (t) { return t.classList.contains('is-current'); })[0] || tiles[0];
  }

  /* --- Boot -------------------------------------------------------------- */

  initHero();
  initProduct();
  initCollection();
  initAccordion();
  initExitIntent();
  initLoot();
})();
